import { Fragment, useLayoutEffect, useState } from 'react'
import { useInView } from '#app/components/ui/reveal.tsx'
import { cn } from '#app/utils/misc.tsx'

const stagger = 55

/**
 * A run of the text with its own class on every word. A class goes on the
 * words, not around them: a `text-gradient-brand` around moving words
 * paints none of them.
 */
export type WordRevealPart = { text: string; className?: string }

/**
 * WordReveal: a text whose words slide up out of their own mask, one after
 * the other, once in view. The server render shows every word in place, so a
 * page's h1 reads before any script runs; the mask only applies once
 * hydrated. The split words are `aria-hidden` and an `sr-only` copy carries
 * the text, so a heading around it is announced as one text. With reduced
 * motion the words stand in place without a transition, so the stagger delay
 * holds none of them back. A text in parts staggers as one sequence.
 */
export function WordReveal({
	text,
	delay = 0,
	className,
	wordClassName,
}: {
	text: string | WordRevealPart[]
	/** ms before the first word moves */
	delay?: number
	className?: string
	/** on every word, e.g. `text-gradient-brand` */
	wordClassName?: string
}) {
	const [ref, inView] = useInView<HTMLSpanElement>()
	// Armed before the first hydrated paint, so a heading already in view is
	// painted masked once and its observer then lets it rise.
	const [armed, setArmed] = useState(false)
	useLayoutEffect(() => {
		if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			setArmed(true)
		}
	}, [])
	const masked = armed && !inView

	const parts = typeof text === 'string' ? [{ text }] : text
	const words = parts.flatMap((part) =>
		part.text
			.split(/\s+/)
			.filter(Boolean)
			.map((word) => ({ word, className: part.className })),
	)

	return (
		<span ref={ref} data-motion="word-reveal" className={className}>
			<span className="sr-only">
				{parts.map((part) => part.text).join(' ')}
			</span>
			<span aria-hidden="true">
				{words.map(({ word, className: partClassName }, i) => (
					<Fragment key={i}>
						{i > 0 ? ' ' : null}
						{/* The word's own padding stretches its gradient over the
						    descenders, the mask's padding keeps them inside the mask,
						    and the negative margin takes both back off the line height. */}
						<span className="-mb-[0.35em] inline-block overflow-hidden pb-[0.1em] align-bottom">
							<span
								data-slot="word"
								className={cn(
									'duration-medium ease-out-expo inline-block pb-[0.25em] transition-transform motion-reduce:transition-none',
									masked
										? 'translate-y-[115%] transition-none'
										: 'translate-y-0',
									wordClassName,
									partClassName,
								)}
								style={{ transitionDelay: `${delay + i * stagger}ms` }}
							>
								{word}
							</span>
						</span>
					</Fragment>
				))}
			</span>
		</span>
	)
}
